
import pathlib from 'path';

import * as ENGINE from '@gnsx/genesys.js';
import { AssetPath, type PropertyMetadata, type ResourceData } from '@gnsx/genesys.js';
import { isEmpty } from 'es-toolkit/compat';
import { toast } from 'sonner';

import { analyzeDependencies, type DependencyAnalysisResult, type PrefabInstanceReference } from '../assetDependencyAnalyzer';
import { createUpdatePlan, executeUpdatePlan } from '../assetPathUpdater';

import { areScenePathsEquivalent, resolveDefaultSceneSettingsUpdate, resolveSceneReloadPathAfterRename, resolveSceneScreenshotMigrationTargets } from './sceneRenameUtils';


import type * as THREE from 'three';

import { sdk } from '@/app/sdk';
import { JS_CLASSES_DIR_NAME } from '@/const';
import { editorActions, nameHelpers, prefabActions, sceneActions, sceneHandler } from '@/editor/core';
import { showAssetInAssetBrowser } from '@/editor/core/actions/assetBrowserReveal';
import { type IAssetFile } from '@/editor/core/assetHandlers/assetHandler';
import { AssetType, isModelAssetPath } from '@/editor/core/assetTypes';
import { threeMaterialLogRef } from '@/editor/core/engineLogRefs';
import { getSceneUndoRedo, resolvePrefabUndoStackKey, resolveSceneUndoStackKey } from '@/editor/core/undoRedo';
import { extractAnimationsFromGltf } from '@/editor/core/utils/gltfAnimationExtractor';
import { assetsStore, editorStore, sceneStore } from '@/editor/store';
import { deleteFromIndexedDB, getLegacySceneScreenshotIdbKey, getProjectScopedSceneScreenshotIdbKey, loadFromIndexedDB, openIndexedDB, saveToIndexedDB } from '@/editor/store/sceneStore';


/**
 * Asset actions
 */

/**
 * Check if asset type is a class type (SourceCode or JsClass)
 */
function isClassAssetType(assetType: AssetType | AssetType[]): boolean {
  return (Array.isArray(assetType) && (assetType.includes(AssetType.SourceCode) || assetType.includes(AssetType.JsClass))) ||
    (!Array.isArray(assetType) && (assetType === AssetType.SourceCode || assetType === AssetType.JsClass));
}

/**
 * Get regex pattern for asset name validation based on asset type
 */
function getAssetNameRegex(assetType: AssetType | AssetType[]): RegExp {
  return isClassAssetType(assetType)
    ? /^[A-Z][a-zA-Z0-9_]*$/ // PascalCase: starts with capital, only letters, numbers and underscores
    : /^[a-zA-Z0-9_-]+$/; // Default: cross-platform filesystem safe characters, no dots
}

/**
 * Filter and validate asset name based on asset type
 * @param name - The name to filter
 * @param assetType - The type(s) of asset to validate against
 * @param allowSpaces - Whether to allow spaces in the name
 */
export function filterValidAssetName(name: string, assetType: AssetType | AssetType[], allowSpaces: boolean = false): string {
  if (isClassAssetType(assetType)) {
    return name
      .replace(/[^a-zA-Z0-9_]/g, '') // Remove all non-alphanumeric chars except underscore
      .replace(/^[a-z]|\s+[a-z]/g, letter => letter.toUpperCase()) // Capitalize first letter and letters after spaces
      .replace(/^[^a-zA-Z]/, ''); // Ensure starts with letter
  }

  // Default case: allow cross-platform filesystem safe characters
  return name
    .replace(/[<>:"/\\|?*\.]|[^\x20-\x7E]/g, allowSpaces ? ' ' : '') // Remove invalid filesystem chars and non-printable chars
    .replace(/^\s+|\s+$/g, '') // Trim leading/trailing spaces
    .replace(/\s+/g, allowSpaces ? ' ' : ''); // Replace multiple spaces with single space if allowed
}

/**
 * Check if an asset name is valid
 * @param name - The new name of the asset
 * @param path - The path to save the asset to
 * @param assetType - The type(s) of asset to create
 * @returns {isValid: boolean, errorMsg: string} - Whether the name is valid and an error message if not
 */
export function checkValidNewAssetName(name: string, path: string, assetType: AssetType | AssetType[]): { isValid: boolean, errorMsg: string } {
  if (!name.trim()) {
    return { isValid: false, errorMsg: 'Name is required.' };
  }
  if (!path) {
    return { isValid: false, errorMsg: 'Must select a directory.' };
  }
  if (isEmpty(assetType)) {
    return { isValid: false, errorMsg: 'Must select an asset type.' };
  }

  const regex = getAssetNameRegex(assetType);
  if (!regex.test(name)) {
    return {
      isValid: false,
      errorMsg: isClassAssetType(assetType)
        ? 'Name must start with an uppercase letter and contain only letters and numbers'
        : 'Name must contain only letters, numbers, hyphens (-) and underscores (_)'
    };
  }

  // Check if asset already exists, case insensitive
  const assetPathNoExt = nameHelpers.joinPaths(path, name).toLowerCase();
  const assetFiles = assetsStore.assetTree.getAssetFiles(assetType);
  const existingAsset = assetFiles.find(asset => asset.path.toLowerCase().startsWith(assetPathNoExt));
  if (existingAsset) {
    return { isValid: false, errorMsg: 'An asset with this name already exists in the selected directory.' };
  }

  return { isValid: true, errorMsg: '' };
}

function inferAssetTypesFromPath(assetPath: string): AssetType[] {
  const lowerPath = assetPath.toLowerCase();
  const inferredTypes = new Set<AssetType>();

  if (isModelAssetPath(lowerPath)) {
    inferredTypes.add(AssetType.Model);
  }
  if (lowerPath.endsWith('.png') || lowerPath.endsWith('.jpg') || lowerPath.endsWith('.jpeg') || lowerPath.endsWith('.webp')) {
    inferredTypes.add(AssetType.Texture);
  }
  if (lowerPath.endsWith('.hdr') || lowerPath.endsWith('.exr')) {
    inferredTypes.add(AssetType.HDRI);
  }
  if (lowerPath.endsWith(ENGINE.PREFAB_EXTENSION)) {
    inferredTypes.add(AssetType.Prefab);
  }
  if (lowerPath.endsWith(ENGINE.MATERIAL_EXTENSION)) {
    inferredTypes.add(AssetType.Material);
  }
  if (lowerPath.endsWith(ENGINE.SCENE_NAME_EXT)) {
    inferredTypes.add(AssetType.Scene);
  }
  if (lowerPath.endsWith('.res.json') || lowerPath.endsWith('.resource.json')) {
    inferredTypes.add(AssetType.Resource);
  }
  if (lowerPath.endsWith('.json')) {
    inferredTypes.add(AssetType.Json);
  }

  return Array.from(inferredTypes);
}

function normalizeAssetTypes(assetType?: AssetType | AssetType[]): AssetType[] {
  if (!assetType) {
    return [];
  }
  return Array.isArray(assetType) ? assetType : [assetType];
}

export function invalidateAssetCachesForPath(assetPath: string, assetType?: AssetType | AssetType[]): void {
  const resolvedTypes = normalizeAssetTypes(assetType);
  const cacheTypes = resolvedTypes.length > 0 ? resolvedTypes : inferAssetTypesFromPath(assetPath);

  cacheTypes.forEach(type => {
    switch (type) {
      case AssetType.Model:
        ENGINE.resourceManager.invalidateModelCache(assetPath);
        break;
      case AssetType.Material:
        ENGINE.resourceManager.invalidateMaterialCache(assetPath);
        ENGINE.resourceManager.invalidateJsonObjectCache(assetPath);
        break;
      case AssetType.Texture:
      case AssetType.HDRI:
        ENGINE.resourceManager.invalidateTextureCache(assetPath);
        break;
      case AssetType.Prefab:
      case AssetType.Scene:
      case AssetType.Json:
      case AssetType.MeshCombination:
      case AssetType.NavMesh:
      case AssetType.AnimationConfig:
      case AssetType.SkeletonProfile:
      case AssetType.VFX:
      case AssetType.Voxel:
        ENGINE.resourceManager.invalidateJsonObjectCache(assetPath);
        break;
      case AssetType.Resource:
        ENGINE.resourceManager.invalidateResourceCache(assetPath);
        break;
      default:
        break;
    }
  });

  editorStore.thumbnailService?.invalidateThumbnailCache([assetPath]);
}

/**
 * Create a material asset from a THREE.Material
 * @param material - The material to create an asset from
 * @param name - The name of the material
 * @param path - The path to save the material asset to
 */
export async function createMaterialAsset(material: THREE.Material, name: string, path: string) {
  try {
    const storageProvider = editorStore.project.makeScopedStorageProvider();
    const targetDirectory = path;
    const filelist = await storageProvider.listFiles(AssetPath.fromString(targetDirectory));
    const fileExt = ENGINE.MATERIAL_EXTENSION;

    // Ensure unique name
    const existingNames = filelist.files.filter(file => file.name.endsWith(fileExt)).map(file => file.name.replace(fileExt, ''));
    const newName = nameHelpers.makeUniqueName(name, existingNames);
    const assetPath = AssetPath.join(targetDirectory, `${newName}${fileExt}`);
    const materialData = ENGINE.createDumper({ flags: ENGINE.DumperFlags.AsPrefab }).dump(material);
    const materialJson = JSON.stringify(materialData, null, 2);

    await getSceneUndoRedo().recordAction(
      'Create Material Asset',
      async () => {
        await storageProvider.uploadFile(AssetPath.fromString(assetPath), new Blob([materialJson], { type: 'application/json' }));
        editorActions.notifyChanged('CreateMaterialAsset', { name, path: assetPath });
        toast.success('Material asset created', { description: `Name: ${name}, Path: ${assetPath}` });

        await assetsStore.assetTree.refreshDirectory(targetDirectory);
        showAssetInAssetBrowser(assetPath, { selectIfFound: true });
      },
      async () => {
        await deleteAssetInternal(assetPath);
      },
    );
  } catch (error) {
    console.error('Failed to create material asset', { name, path, error });
    toast.error('Failed to create material asset', { description: `Name: ${name}, Path: ${path}` });
  }
}

export async function loadMaterialAsset(asset: IAssetFile): Promise<THREE.Material | null> {
  return await ENGINE.resourceManager.loadMaterial(ENGINE.AssetPath.fromString(asset.path));
}

export interface SaveMaterialAssetOptions {
  silent?: boolean;
  reloadDependents?: boolean;
}

async function findModelAssetsReferencingMaterialPath(materialPath: string): Promise<IAssetFile[]> {
  const modelAssets = assetsStore.assetTree
    .getAssetFiles(AssetType.Model)
    .filter(assetFile => assetFile.isProjectItem);

  if (modelAssets.length === 0) {
    return [];
  }

  const storageProvider = editorStore.project.makeScopedStorageProvider();
  const dependentModels: IAssetFile[] = [];

  for (const modelAsset of modelAssets) {
    try {
      const modelBuffer = await storageProvider.downloadFileAsBuffer(ENGINE.AssetPath.fromString(modelAsset.path));
      const modelText = new TextDecoder().decode(new Uint8Array(modelBuffer));
      if (modelText.includes(materialPath)) {
        dependentModels.push(modelAsset);
      }
    } catch (error) {
      console.warn('[assetActions] Failed to scan model for material dependency', {
        modelPath: modelAsset.path,
        materialPath,
        error,
      });
    }
  }

  return dependentModels;
}

async function refreshDependentModelAssetsForMaterialPath(materialPath: string, reloadDependents: boolean): Promise<void> {
  const dependentModels = await findModelAssetsReferencingMaterialPath(materialPath);
  if (dependentModels.length === 0) {
    return;
  }

  const dependentModelPaths = dependentModels.map(model => model.path);
  editorStore.thumbnailService?.invalidateThumbnailCache(dependentModelPaths);

  for (const modelAsset of dependentModels) {
    invalidateAssetCachesForPath(modelAsset.path, AssetType.Model);
    modelAsset.setDirty();
    modelAsset.refreshPreview();
    editorStore.thumbnailService?.queueThumbnailGeneration(modelAsset, 100, true);

    if (reloadDependents) {
      await sceneActions.reloadActorsReferencingAsset(modelAsset.path);
    }
  }
}

export function resolveSaveMaterialAssetOptions(options: SaveMaterialAssetOptions = {}): Required<SaveMaterialAssetOptions> {
  return {
    silent: options.silent ?? true,
    reloadDependents: options.reloadDependents ?? false,
  };
}

export async function saveMaterialAsset(
  asset: IAssetFile,
  material: THREE.Material,
  options: SaveMaterialAssetOptions = {}
) {
  const { silent, reloadDependents } = resolveSaveMaterialAssetOptions(options);

  if (!material.userData?.isDirty) return;

  try {
    delete material.userData.isDirty;
    const materialData = ENGINE.WorldSerializer.serializeMaterial(material);
    const materialJson = JSON.stringify(materialData, null, 2);

    const storageProvider = editorStore.project.makeScopedStorageProvider();
    const result = await storageProvider.uploadFile(ENGINE.AssetPath.fromString(asset.path), new Blob([materialJson], { type: 'application/json' }));
    // console.log('Saved material asset', { asset: asset.path, material, materialJson, result });

    invalidateAssetCachesForPath(asset.path, AssetType.Material);
    // Material updates need fresh model-with-overrides clones across preview/model-viewer/PIE.
    ENGINE.resourceManager.invalidateModelCache(undefined, false, true);
    editorStore.thumbnailService?.invalidateThumbnailCache([asset.path]);
    asset.setDirty(true, { updateThumbnail: false, updatePreview: true });
    // Refresh thumbnail
    editorStore.thumbnailService?.queueThumbnailGeneration(asset, 100, true);
    if (reloadDependents) {
      await refreshDependentModelAssetsForMaterialPath(asset.path, true);
    }

    if (reloadDependents) {
      await sceneActions.reloadActorsReferencingAsset(asset.path);
    }

    if (!silent) {
      editorActions.notifyChanged('SaveMaterialAsset', { name: asset.name, path: asset.path });
      toast.success('Material asset saved', { description: `Name: ${asset.name}, Path: ${asset.path}` });
    }
  } catch (error) {
    material.userData.isDirty = true;
    console.warn('Failed to save material asset', { path: asset.path, name: asset.name, material: threeMaterialLogRef(material), error });
    if (!silent) {
      toast.error('Failed to save material asset', { description: `Name: ${asset.name}, Path: ${asset.path}` });
    }
  }
}

/**
 * Create an Animation Config asset
 * @param name - The name of the animation config
 * @param path - The path to save the animation config asset to
 */
export async function createAnimationConfigAsset(name: string, path: string) {
  try {
    const undoRedo = getSceneUndoRedo();
    const storageProvider = editorStore.project.makeScopedStorageProvider();
    const targetDirectory = path;
    const filelist = await storageProvider.listFiles(AssetPath.fromString(targetDirectory));
    const fileExt = ENGINE.ANIMATION_CONFIG_RESOURCE_EXTENSION;

    const existingNames = filelist.files.filter(file => file.name.endsWith(fileExt)).map(file => file.name.replace(fileExt, ''));
    const newName = nameHelpers.makeUniqueName(name, existingNames);
    const assetPath = AssetPath.join(targetDirectory, `${newName}${fileExt}`);

    const defaultConfig: ENGINE.AnimationStateMachineConfig = {
      parameters: {},
      initialState: '',
      externalAnimationSources: [],
      oneShotClips: [],
      states: {},
      clipOptions: {}
    };

    const resource = ENGINE.AnimationConfigResource.fromConfig(defaultConfig);
    const serializedData = ENGINE.WorldSerializer.exportObject(resource);
    const configJson = JSON.stringify(serializedData, null, 2);

    await undoRedo.recordAction(
      'Create Animation Config Asset',
      async () => {
        await storageProvider.uploadFile(AssetPath.fromString(assetPath), new Blob([configJson], { type: 'application/json' }));
        editorActions.notifyChanged('CreateAnimationConfigAsset', { name, path: assetPath });
        toast.success('Animation config created', { description: `Name: ${name}, Path: ${assetPath}` });

        await assetsStore.assetTree.refreshDirectory(targetDirectory);
        showAssetInAssetBrowser(assetPath, { selectIfFound: true });
        editorStore.openAnimationConfigEditor(assetPath);
      },
      async () => {
        await deleteAssetInternal(assetPath);
      },
    );
  } catch (error) {
    console.error('[assetActions] Failed to create animation config asset:', error);
    toast.error(`Failed to create animation config: ${error instanceof Error ? error.message : 'Unknown error'}`);
    throw error;
  }
}

/**
 * Create an Animation Config asset from a GLTF model with animations
 * @param modelAsset - The GLTF model asset
 * @param name - The name for the animation config
 */
export async function createAnimationConfigFromModel(modelAsset: IAssetFile, name: string) {
  try {
    const undoRedo = getSceneUndoRedo();
    const storageProvider = editorStore.project.makeScopedStorageProvider();

    const modelPath = modelAsset.path;
    const targetDirectory = pathlib.dirname(modelPath);

    const assetPath = ENGINE.AssetPath.fromString(modelPath);
    const resolved = await ENGINE.resolvePath(assetPath);
    const modelUrl = resolved.getResolvedPath();

    const extractionResult = await extractAnimationsFromGltf(modelUrl);

    if (extractionResult.animationCount === 0) {
      toast.error('No animations found in model', {
        description: `The model "${modelAsset.name}" does not contain any animation clips.`
      });
      return;
    }

    const filelist = await storageProvider.listFiles(AssetPath.fromString(targetDirectory));
    const fileExt = ENGINE.ANIMATION_CONFIG_RESOURCE_EXTENSION;

    const existingNames = filelist.files.filter(file => file.name.endsWith(fileExt)).map(file => file.name.replace(fileExt, ''));
    const newName = nameHelpers.makeUniqueName(name, existingNames);
    const configPath = AssetPath.join(targetDirectory, `${newName}${fileExt}`);

    // Create a minimal config and let users author states explicitly in the editor.
    // This avoids generating a large graph automatically when importing a model with many clips.
    const animConfig: ENGINE.AnimationStateMachineConfig = {
      parameters: {},
      initialState: '',
      externalAnimationSources: [],
      oneShotClips: [],
      states: {},
      clipOptions: {}
    };

    const resource = ENGINE.AnimationConfigResource.fromConfig(animConfig);
    const serializedData = ENGINE.WorldSerializer.exportObject(resource);
    const configJson = JSON.stringify(serializedData, null, 2);

    await undoRedo.recordAction(
      'Create Animation Config from Model',
      async () => {
        await storageProvider.uploadFile(AssetPath.fromString(configPath), new Blob([configJson], { type: 'application/json' }));
        editorActions.notifyChanged('CreateAnimationConfigFromModel', { name: newName, path: configPath, modelPath });

        toast.success('Animation config created', {
          description: `Created "${newName}" with no auto-generated states (${extractionResult.animationCount} clips detected)`
        });

        await assetsStore.assetTree.refreshDirectory(targetDirectory);
        showAssetInAssetBrowser(configPath, { selectIfFound: true });
        editorStore.openAnimationConfigEditor(configPath, modelPath);
      },
      async () => {
        await deleteAssetInternal(configPath);
      },
    );
  } catch (error) {
    console.error('[assetActions] Failed to create animation config from model:', error);
    toast.error(`Failed to create animation config: ${error instanceof Error ? error.message : 'Unknown error'}`);
    throw error;
  }
}

interface MigrateLegacyAnimationConfigOptions {
  sourcePath: string;
  targetPath?: string;
  updateReferences?: boolean;
  openMigratedAsset?: boolean;
  modelPath?: string;
  configOverride?: ENGINE.AnimationStateMachineConfig;
}

interface MigrateLegacyAnimationConfigResult {
  targetPath: string;
  referencesUpdated: number;
}

export async function migrateLegacyAnimationConfigAsset(
  options: MigrateLegacyAnimationConfigOptions
): Promise<MigrateLegacyAnimationConfigResult> {
  const {
    sourcePath,
    targetPath,
    updateReferences = false,
    openMigratedAsset = true,
    modelPath,
    configOverride,
  } = options;

  if (!ENGINE.isLegacyAnimationConfigPath(sourcePath)) {
    throw new Error(`Expected a legacy animation config path, received: ${sourcePath}`);
  }

  const storageProvider = editorStore.project.makeScopedStorageProvider();

  const runtimeConfig = configOverride ?? await loadLegacyAnimationConfig(sourcePath);
  const finalTargetPath = targetPath ?? await getUniqueMigratedAnimationConfigPath(sourcePath);
  const targetAssetPath = ENGINE.AssetPath.fromString(finalTargetPath);

  const resource = ENGINE.AnimationConfigResource.fromConfig(runtimeConfig);
  const serializedData = ENGINE.WorldSerializer.exportObject(resource);
  const resourceJson = JSON.stringify(serializedData, null, 2);

  await storageProvider.uploadFile(targetAssetPath, new Blob([resourceJson], { type: 'application/json' }));

  let referencesUpdated = 0;
  if (updateReferences) {
    const updatePlan = await createUpdatePlan(sourcePath, finalTargetPath);
    referencesUpdated = updatePlan.updateOperations.length;
    await executeUpdatePlan(updatePlan);
  }

  const sourceDirectory = pathlib.dirname(sourcePath);
  const targetDirectory = pathlib.dirname(finalTargetPath);
  await assetsStore.assetTree.refreshDirectory(sourceDirectory);
  if (targetDirectory !== sourceDirectory) {
    await assetsStore.assetTree.refreshDirectory(targetDirectory);
  }
  assetsStore.setCurrentDirectory(targetDirectory);
  editorStore.setSelectedAssetItemByPath(finalTargetPath);

  if (openMigratedAsset) {
    editorStore.openAnimationConfigEditor(finalTargetPath, modelPath);
  }

  editorActions.notifyChanged('MigrateAnimationConfigAsset', {
    sourcePath,
    targetPath: finalTargetPath,
    updateReferences,
    referencesUpdated,
  });

  return {
    targetPath: finalTargetPath,
    referencesUpdated,
  };
}

async function loadLegacyAnimationConfig(sourcePath: string): Promise<ENGINE.AnimationStateMachineConfig> {
  const storageProvider = editorStore.project.makeScopedStorageProvider();
  const content = await storageProvider.downloadFileAsText(ENGINE.AssetPath.fromString(sourcePath));
  if (!content || content.trim() === '') {
    throw new Error('Legacy animation config file is empty');
  }
  return ENGINE.parseJson(content) as ENGINE.AnimationStateMachineConfig;
}

async function getUniqueMigratedAnimationConfigPath(sourcePath: string): Promise<string> {
  const directory = pathlib.dirname(sourcePath);
  const baseName = pathlib.basename(sourcePath).replace(/\.anim\.json$/i, '');
  const fileExt = ENGINE.ANIMATION_CONFIG_RESOURCE_EXTENSION;
  const storageProvider = editorStore.project.makeScopedStorageProvider();
  const list = await storageProvider.listFiles(ENGINE.AssetPath.fromString(directory));
  const existingNames = list.files
    .filter(file => file.name.endsWith(fileExt))
    .map(file => file.name.replace(fileExt, ''));
  const uniqueName = nameHelpers.makeUniqueName(baseName, existingNames);
  return ENGINE.AssetPath.join(directory, `${uniqueName}${fileExt}`);
}

/**
 * Create a VFX asset
 * @param name - The name of the VFX
 * @param path - The path to save the VFX asset to
 */
export async function createVFXAsset(
  name: string,
  path: string
) {
  try {
    const undoRedo = getSceneUndoRedo();
    const storageProvider = editorStore.project.makeScopedStorageProvider();
    const targetDirectory = path;
    const filelist = await storageProvider.listFiles(AssetPath.fromString(targetDirectory));
    const fileExt = '.vfx.json';

    const existingNames = filelist.files.filter(file => file.name.endsWith(fileExt)).map(file => file.name.replace(fileExt, ''));
    const newName = nameHelpers.makeUniqueName(name, existingNames);
    const assetPath = AssetPath.join(targetDirectory, `${newName}${fileExt}`);

    const defaultVFX = ENGINE.VFXDefinition.fromJSON({
      version: 1,
      name: newName,
      particles: [
        {
          nbParticles: 1000,
          gravity: [0, 0, 0] as [number, number, number],
          renderMode: 'billboard' as const,
          intensity: 1,
          appearance: 'circular' as const,
          easeFunction: 'easeLinear' as const,
          fadeAlpha: [0, 1] as [number, number],
          fadeSize: [0, 1] as [number, number],
          blendingMode: 'normal' as const
        }
      ],
      emitters: [
        {
          particlesIndex: 0,
          loop: true,
          duration: 1,
          nbParticles: 10,
          spawnMode: 'time' as const,
          delay: 0,
          particlesLifetime: [1, 2] as [number, number],
          startPositionMin: [-0.1, 0, -0.1] as [number, number, number],
          startPositionMax: [0.1, 0, 0.1] as [number, number, number],
          startRotationMin: [0, 0, 0] as [number, number, number],
          startRotationMax: [0, 0, 0] as [number, number, number],
          rotationSpeedMin: [0, 0, 0] as [number, number, number],
          rotationSpeedMax: [0, 0, 0] as [number, number, number],
          directionMin: [0, 1, 0] as [number, number, number],
          directionMax: [0, 1, 0] as [number, number, number],
          size: [0.1, 0.1] as [number, number],
          speed: [1, 1] as [number, number],
          colorStart: ['#ffffff'] as [string] | [string, string],
          colorEnd: ['#ffffff'] as [string] | [string, string],
          useLocalDirection: false
        }
      ]
    });

    const serialized = ENGINE.WorldSerializer.serializeResource(defaultVFX);
    const vfxJson = JSON.stringify(serialized, null, 2);

    await undoRedo.recordAction(
      'Create VFX Asset',
      async () => {
        await storageProvider.uploadFile(AssetPath.fromString(assetPath), new Blob([vfxJson], { type: 'application/json' }));
        editorActions.notifyChanged('CreateVFXAsset', { name, path: assetPath });
        toast.success('VFX asset created', { description: `Name: ${name}, Path: ${assetPath}` });

        await assetsStore.assetTree.refreshDirectory(targetDirectory);
        showAssetInAssetBrowser(assetPath, { selectIfFound: true });
      },
      async () => {
        await deleteAssetInternal(assetPath);
      },
    );

  } catch (error) {
    console.error('Failed to create VFX asset', { name, path, error });
    toast.error('Failed to create VFX asset', { description: `Name: ${name}, Path: ${path}` });
  }
}

/**
 * Create a Skeleton Profile asset
 * @param name - The name of the skeleton profile
 * @param path - The path to save the skeleton profile asset to
 * @returns The full asset path of the created profile
 */
export async function createSkeletonProfileAsset(name: string, path: string): Promise<string> {
  try {
    const undoRedo = getSceneUndoRedo();
    const storageProvider = editorStore.project.makeScopedStorageProvider();
    const targetDirectory = path;
    const filelist = await storageProvider.listFiles(AssetPath.fromString(targetDirectory));
    const fileExt = '.skeleton-profile.json';

    const existingNames = filelist.files.filter(file => file.name.endsWith(fileExt)).map(file => file.name.replace(fileExt, ''));
    const newName = nameHelpers.makeUniqueName(name, existingNames);
    const assetPath = AssetPath.join(targetDirectory, `${newName}${fileExt}`);

    const defaultProfile: ENGINE.SkeletonProfile = {
      name: newName,
      description: 'Custom skeleton profile',
      hipBoneName: '',
      boneToRole: {},
      forceTPose: false,
    };

    const profileJson = JSON.stringify(defaultProfile, null, 2);

    await undoRedo.recordAction(
      'Create Skeleton Profile Asset',
      async () => {
        await storageProvider.uploadFile(AssetPath.fromString(assetPath), new Blob([profileJson], { type: 'application/json' }));
        editorActions.notifyChanged('CreateSkeletonProfileAsset', { name, path: assetPath });
        toast.success('Skeleton profile created', { description: `Name: ${name}, Path: ${assetPath}` });

        await assetsStore.assetTree.refreshDirectory(targetDirectory);
        showAssetInAssetBrowser(assetPath, { selectIfFound: true });
      },
      async () => {
        await deleteAssetInternal(assetPath);
      },
    );

    return assetPath;
  } catch (error) {
    console.error('Failed to create skeleton profile asset', { name, path, error });
    toast.error('Failed to create skeleton profile asset', { description: `Name: ${name}, Path: ${path}` });
    throw error;
  }
}

/**
 * Save a Skeleton Profile asset (update existing or create new)
 * @param profile - The skeleton profile data
 * @param assetPath - The full asset path to save to
 */
export async function saveSkeletonProfileAsset(profile: ENGINE.SkeletonProfile, assetPath: string): Promise<void> {
  try {
    const storageProvider = editorStore.project.makeScopedStorageProvider();
    const profileJson = JSON.stringify(profile, null, 2);

    await storageProvider.uploadFile(AssetPath.fromString(assetPath), new Blob([profileJson], { type: 'application/json' }));

    // Refresh the directory containing the file
    const pathParts = assetPath.split('/');
    pathParts.pop(); // Remove filename
    const directory = pathParts.join('/');
    if (directory) {
      await assetsStore.assetTree.refreshDirectory(directory);
    }

    editorActions.notifyChanged('SaveSkeletonProfileAsset', { path: assetPath });
  } catch (error) {
    console.error('Failed to save skeleton profile asset', { assetPath, error });
    throw error;
  }
}

/**
 * Check if a material name is valid
 * @param name - The name of the material
 * @param path - The path to save the material asset to
 * @param selectedMaterialType - The type of material to create
 * @returns {isValid: boolean, errorMsg: string} - Whether the material name is valid and an error message if not
 */
export function checkValidNewMaterialName(name: string, path: string, selectedMaterialType: string): { isValid: boolean, errorMsg: string } {
  if (!selectedMaterialType) {
    return { isValid: false, errorMsg: 'Material type is required' };
  }
  return checkValidNewAssetName(name, path, AssetType.Material);
}

/**
 * Get the path to a material asset
 * @param name - The name of the material
 * @param path - The path to save the material asset to
 * @returns The path to the material asset
 */
export function getMaterialAssetPath(name: string, path: string): string {
  return AssetPath.join(path, `${name}${ENGINE.MATERIAL_EXTENSION}`);
}


async function deleteAssetInternal(assetPath: string, assetType?: AssetType | AssetType[]): Promise<void> {
  invalidateAssetCachesForPath(assetPath, assetType);
  const storageProvider = editorStore.project.makeScopedStorageProvider();
  await storageProvider.deleteItem(AssetPath.fromString(assetPath));
  invalidateAssetCachesForPath(assetPath, assetType);

  // Clear selection if the deleted asset was selected
  if (editorStore.selectedAssetItem?.path === assetPath) {
    editorStore.setSelectedAssetItem(undefined);
  }
}

/**
 * Check if a folder can be deleted
 * @param folderPath - The path of the folder to check
 * @returns true if the folder can be deleted, false otherwise
 */
export function canDeleteFolder(folderPath: string): boolean {
  // Cannot delete read-only (engine) folders
  if (folderPath.startsWith(ENGINE.ENGINE_PATH_PREFIX)) {
    return false;
  }
  // Protected project folders that cannot be deleted
  const protectedFolders = [
    ENGINE.PROJECT_PATH_PREFIX, // @project
    `${ENGINE.PROJECT_PATH_PREFIX}/${ENGINE.ASSETS_FOLDER}`, // @project/assets
    `${ENGINE.PROJECT_PATH_PREFIX}/src`, // @project/src
    `${ENGINE.PROJECT_PATH_PREFIX}/${JS_CLASSES_DIR_NAME}`, // @project/@js-classes
    `${ENGINE.PROJECT_PATH_PREFIX}/${ENGINE.BUILT_PROJECT_FOLDER}`, // @project/.built
  ];
  return !protectedFolders.includes(folderPath);
}

/**
 * Remove prefab instance actors from scene files on disk.
 *
 * Iterates each affected scene's serialized JSON, deletes the matching entries
 * from `$root.actors` (handling both plain-array and object-with-`$length` diff
 * forms), and writes the scene back. If the active scene was modified, it is
 * reloaded so the live world reflects the changes.
 *
 * @returns counts of removed instances and affected scenes
 */
export async function removePrefabInstancesFromScenes(
  instances: readonly PrefabInstanceReference[]
): Promise<{ removedInstances: number; affectedScenes: number }> {
  if (instances.length === 0) {
    return { removedInstances: 0, affectedScenes: 0 };
  }

  const byScene = new Map<string, Set<string>>();
  for (const inst of instances) {
    const keys = byScene.get(inst.scenePath) ?? new Set<string>();
    keys.add(inst.actorKey);
    byScene.set(inst.scenePath, keys);
  }

  const storageProvider = editorStore.project.makeScopedStorageProvider();
  let removedInstances = 0;
  let affectedScenes = 0;

  for (const [scenePath, actorKeys] of byScene) {
    try {
      const data = await storageProvider.downloadFileAsJson<unknown>(
        AssetPath.fromString(scenePath)
      );
      if (!data || typeof data !== 'object') continue;
      const root = (data as { $root?: unknown }).$root;
      if (!root || typeof root !== 'object') continue;
      const actors = (root as { actors?: unknown }).actors;
      if (!actors || typeof actors !== 'object') continue;

      let removedThisScene = 0;
      if (Array.isArray(actors)) {
        const indicesToRemove = new Set<number>();
        for (const k of actorKeys) {
          const idx = Number(k);
          if (Number.isInteger(idx) && idx >= 0 && idx < actors.length) {
            indicesToRemove.add(idx);
          }
        }
        const next: unknown[] = [];
        actors.forEach((value, idx) => {
          if (indicesToRemove.has(idx)) {
            removedThisScene++;
          } else {
            next.push(value);
          }
        });
        (root as { actors: unknown }).actors = next;
      } else {
        const actorsRecord = actors as Record<string, unknown>;
        for (const k of actorKeys) {
          if (k in actorsRecord) {
            delete actorsRecord[k];
            removedThisScene++;
          }
        }
      }

      if (removedThisScene > 0) {
        const fileJson = JSON.stringify(data, null, 2);
        await storageProvider.uploadFile(
          AssetPath.fromString(scenePath),
          new Blob([fileJson], { type: 'application/json' })
        );
        const sceneAsset = assetsStore.assetTree.files.get(scenePath);
        sceneAsset?.setDirty();
        removedInstances += removedThisScene;
        affectedScenes++;
      }
    } catch (error) {
      console.warn(`Failed to remove prefab instances from scene ${scenePath}:`, error);
    }
  }

  const currentScene = editorStore.project.currentScene;
  if (currentScene) {
    const matchingPath = Array.from(byScene.keys()).find(p => areScenePathsEquivalent(p, currentScene));
    if (matchingPath) {
      try {
        await sceneHandler.loadScene(AssetPath.fromString(currentScene));
      } catch (error) {
        console.warn('Failed to reload active scene after instance removal:', error);
      }
    }
  }

  return { removedInstances, affectedScenes };
}

/**
 * Delete an asset file
 * @param asset - The asset to delete
 */
export async function deleteAsset(asset: IAssetFile): Promise<void> {
  try {
    // Check if folder can be deleted
    if (asset.isFolder && !canDeleteFolder(asset.path)) {
      toast.error('Cannot delete folder', {
        description: 'This is a protected system folder and cannot be deleted'
      });
      return;
    }

    const undoRedo = getSceneUndoRedo();
    const storageProvider = editorStore.project.makeScopedStorageProvider();

    // Backup file content before deletion for undo functionality
    let fileContent: Blob | string | ArrayBuffer | null = null;
    try {
      fileContent = await storageProvider.downloadFile(AssetPath.fromString(asset.path));
    } catch (error) {
      console.warn('Failed to backup file content before deletion', { asset: asset.path, error });
    }

    const assetPath = asset.path;
    const assetName = asset.name;
    const assetType = asset.assetType;
    const parentDirectory = assetPath.substring(0, assetPath.lastIndexOf('/'));

    await undoRedo.recordAction(
      'Delete Asset',
      async () => {
        await deleteAssetInternal(assetPath, assetType);
        editorActions.notifyChanged('DeleteAsset', { name: assetName, path: assetPath });
        toast.success('Asset deleted', { description: `${assetName} has been deleted` });
      },
      async () => {
        if (fileContent) {
          try {
            await storageProvider.uploadFile(AssetPath.fromString(assetPath), fileContent);
            invalidateAssetCachesForPath(assetPath, assetType);

            await assetsStore.assetTree.refreshDirectory(parentDirectory || '/');
            editorStore.setSelectedAssetItemByPath(assetPath);

            editorActions.notifyChanged('RestoreAsset', { name: assetName, path: assetPath });
            toast.success('Asset restored', { description: `${assetName} has been restored` });
          } catch (error) {
            console.error('Failed to restore asset', { asset: assetPath, error });
            toast.error('Failed to restore asset', {
              description: error instanceof Error ? error.message : 'Unknown error occurred'
            });
            throw error;
          }
        } else {
          toast.error('Cannot restore asset', { description: 'File content was not backed up before deletion' });
          throw new Error('File content was not backed up before deletion');
        }
      },
    );

  } catch (error) {
    console.error('Failed to delete asset', { asset: asset.path, error });
    toast.error('Failed to delete asset', {
      description: error instanceof Error ? error.message : 'Unknown error occurred'
    });
    throw error;
  }
}

/**
 * Create a new folder
 * @param name - The name of the folder to create
 * @param path - The parent directory path where the folder should be created
 * @param openAfterCreate - Whether to navigate to the newly created folder directory after creation
 */
export async function createFolder(name: string, path: string, openAfterCreate: boolean = false): Promise<void> {
  try {
    const undoRedo = getSceneUndoRedo();
    const storageProvider = editorStore.project.makeScopedStorageProvider();

    let uniqueName = name;
    try {
      const listing = await storageProvider.listFiles(AssetPath.fromString(path));
      const existingNames = listing.directories.map(dir => dir.name);
      uniqueName = nameHelpers.makeUniqueName(name, existingNames);
    } catch (listError) {
      console.warn('Could not list directory for unique name generation, using original name', { path, error: listError });
    }

    const folderPath = nameHelpers.joinPaths(path, uniqueName);

    await undoRedo.recordAction(
      'Create Folder',
      async () => {
        try {
          await storageProvider.createDirectory(AssetPath.fromString(folderPath));

          await assetsStore.assetTree.refreshDirectory(path);

          if (openAfterCreate) {
            assetsStore.setCurrentDirectory(folderPath);
          } else {
            showAssetInAssetBrowser(folderPath, { selectIfFound: true });
          }

          editorActions.notifyChanged('CreateFolder', { name: uniqueName, path: folderPath });
          toast.success('Folder created', { description: `Name: ${uniqueName}, Path: ${folderPath}` });
        } catch (createError) {
          console.error('Failed to create folder in do method', { name: uniqueName, path: folderPath, error: createError });
          toast.error('Failed to create folder', {
            description: createError instanceof Error ? createError.message : 'Unknown error occurred'
          });
          throw createError;
        }
      },
      async () => {
        try {
          await storageProvider.deleteItem(AssetPath.fromString(folderPath));

          await assetsStore.assetTree.refreshDirectory(path);

          editorActions.notifyChanged('DeleteFolder', { name: uniqueName, path: folderPath });
        } catch (deleteError) {
          console.error('Failed to delete folder in undo method', { name: uniqueName, path: folderPath, error: deleteError });
          toast.error('Failed to undo folder creation', {
            description: deleteError instanceof Error ? deleteError.message : 'Unknown error occurred'
          });
          throw deleteError;
        }
      },
    );

  } catch (error) {
    console.error('Failed to create folder', { name, path, error });
    toast.error('Failed to create folder', {
      description: error instanceof Error ? error.message : 'Unknown error occurred'
    });
    throw error;
  }
}

/**
 * Check if a folder name is valid
 * @param name - The name of the folder
 * @param path - The parent directory path
 * @returns Promise<{isValid: boolean, errorMsg: string}> - Whether the folder name is valid and an error message if not
 */
export async function checkValidNewFolderName(name: string, path: string): Promise<{ isValid: boolean, errorMsg: string }> {
  if (!name.trim()) {
    return { isValid: false, errorMsg: 'Folder name is required.' };
  }
  if (!path) {
    return { isValid: false, errorMsg: 'Must select a parent directory.' };
  }

  // Use filesystem safe characters (no dots to avoid confusion with file extensions)
  const regex = /^[a-zA-Z0-9_-]+$/;
  if (!regex.test(name)) {
    return {
      isValid: false,
      errorMsg: 'Folder name must contain only letters, numbers, hyphens (-) and underscores (_)'
    };
  }

  try {
    // Check if folder already exists by attempting to list the target directory
    const storageProvider = editorStore.project.makeScopedStorageProvider();
    const folderPath = nameHelpers.joinPaths(path, name);

    try {
      const listing = await storageProvider.listFiles(AssetPath.fromString(path));
      const existingFolder = listing.directories.find(dir =>
        dir.name.toLowerCase() === name.toLowerCase()
      );

      if (existingFolder) {
        return { isValid: false, errorMsg: 'A folder with this name already exists in the selected directory.' };
      }
    } catch (listError) {
      // If we can't list the directory, the parent path might not exist
      console.warn('Could not list parent directory for folder validation', { path, error: listError });
      return { isValid: false, errorMsg: 'Parent directory is not accessible.' };
    }

    return { isValid: true, errorMsg: '' };
  } catch (error) {
    console.warn('Error validating folder name', { name, path, error });
    // If there's an error checking, we'll allow creation and let the actual creation handle the error
    return { isValid: true, errorMsg: '' };
  }
}

/**
 * Show an item (file or folder) in the file explorer
 * @param item - The item to show in the file explorer
 */
export function showItemInExplorer(item: IAssetFile | undefined) {
  if (!item) return;
  sdk?.os.showItemInFolder(item.absolutePath);
}

/**
 * Editor-specific extension of engine's ResourceData
 * Adds editor tracking fields on top of the engine's ResourceData structure
 */
export interface EditorResourceData extends ResourceData {
  isDirty?: boolean;  // Tracks if resource needs to be saved
}

// Re-export engine types for convenience
export type { PropertyMetadata, ResourceData };

/**
 * Load a resource asset from storage
 * Uses the engine's ResourceManager to load and parse the resource
 */
export async function loadResourceAsset(asset: IAssetFile): Promise<EditorResourceData | null> {
  try {
    const resourceData = await ENGINE.resourceManager.loadResource(ENGINE.AssetPath.fromString(asset.path));

    if (!resourceData) {
      throw new Error('ResourceManager returned null');
    }

    // Store the asset path on the resource instance for editor use
    // This allows the UniversalAccessor system to find the resource path when needed
    if (resourceData.instance) {
      Object.defineProperty(resourceData.instance, '__editorAssetPath', {
        value: asset.path,
        writable: false,
        enumerable: false,
        configurable: true
      });
    }

    return resourceData as EditorResourceData;
  } catch (error) {
    console.error('Failed to load resource asset', { asset: asset.path, error });
    toast.error('Failed to load resource asset', { description: `Name: ${asset.name}` });
    return null;
  }
}

/**
 * Create a new resource asset from a Resource class
 * @param className - The full class name (e.g., ENGINE.MyResource)
 * @param name - The name of the resource asset
 * @param path - The path to save the resource asset to
 */
export async function createResourceAsset(className: string, name: string, path: string) {
  try {
    const undoRedo = getSceneUndoRedo();
    const storageProvider = editorStore.project.makeScopedStorageProvider();
    const targetDirectory = path;
    const filelist = await storageProvider.listFiles(AssetPath.fromString(targetDirectory));

    const classConstructor = ENGINE.ClassRegistry.getClass(className);
    const resourcePrefix = ENGINE.ClassRegistry.getResourcePrefix(classConstructor) ?? 'res';
    const fileExt = `.${resourcePrefix}.json`;

    const existingNames = filelist.files.filter(file => file.name.endsWith(fileExt)).map(file => file.name.replace(fileExt, ''));
    const newName = nameHelpers.makeUniqueName(name, existingNames);
    const assetPath = AssetPath.join(targetDirectory, `${newName}${fileExt}`);

    const resourceData = ENGINE.resourceManager.createResourceData(className);
    if (!resourceData) {
      throw new Error(`Failed to create resource data for class: ${className}`);
    }

    if (resourceData.instance) {
      Object.defineProperty(resourceData.instance, '__editorAssetPath', {
        value: assetPath,
        writable: false,
        enumerable: false,
        configurable: true
      });
    }

    const serializedData = ENGINE.WorldSerializer.serializeResource(resourceData.instance);
    const resourceJson = JSON.stringify(serializedData, null, 2);

    await undoRedo.recordAction(
      'Create Resource Asset',
      async () => {
        await storageProvider.uploadFile(AssetPath.fromString(assetPath), new Blob([resourceJson], { type: 'application/json' }));
        editorActions.notifyChanged('CreateResourceAsset', { name: newName, path: assetPath, className });
        toast.success('Resource asset created', { description: `Name: ${newName}, Path: ${assetPath}` });

        await assetsStore.assetTree.refreshDirectory(targetDirectory);
        showAssetInAssetBrowser(assetPath, { selectIfFound: true });
      },
      async () => {
        await deleteAssetInternal(assetPath);
      },
    );

  } catch (error) {
    console.error('Failed to create resource asset', { className, name, path, error });
    toast.error('Failed to create resource asset', { description: `Name: ${name}, Class: ${className}` });
  }
}

/**
 * Save a resource asset to storage
 */
export async function saveResourceAsset(asset: IAssetFile, resourceData: EditorResourceData, silent: boolean = true) {
  if (!resourceData.isDirty) return;

  delete resourceData.isDirty;

  // Serialize using WorldSerializer (just like materials)
  const serializedData = ENGINE.WorldSerializer.serializeResource(resourceData.instance);
  const resourceJson = JSON.stringify(serializedData, null, 2);

  try {
    const storageProvider = editorStore.project.makeScopedStorageProvider();
    await storageProvider.uploadFile(ENGINE.AssetPath.fromString(asset.path), new Blob([resourceJson], { type: 'application/json' }));

    asset.setDirty();

    // Reload resource class cache since the class might have changed
    if (typeof asset.loadResourceClass === 'function') {
      await asset.loadResourceClass();
    }

    if (!silent) {
      editorActions.notifyChanged('SaveResourceAsset', { name: asset.name, path: asset.path });
      toast.success('Resource asset saved', { description: `Name: ${asset.name}, Path: ${asset.path}` });
    }
  } catch (error) {
    console.warn('Failed to save resource asset', { asset: asset.path, resourceData, error });
    if (!silent) {
      toast.error('Failed to save resource asset', { description: `Name: ${asset.name}, Path: ${asset.path}` });
    }
  }
}

/**
 * Validation result for asset rename
 */
export interface RenameValidationResult {
  isValid: boolean;
  errorMsg: string;
  newPath: string;
  conflicts: string[];
}

/**
 * Validate that an asset can be renamed to the new name
 * @param asset - The asset to rename
 * @param newName - The new name for the asset
 * @returns Validation result with new path and any conflicts
 */
export function validateRename(asset: IAssetFile, newName: string): RenameValidationResult {
  const result: RenameValidationResult = {
    isValid: false,
    errorMsg: '',
    newPath: '',
    conflicts: [],
  };

  // Can't rename engine assets
  if (asset.isEngineAsset) {
    result.errorMsg = 'Cannot rename engine assets';
    return result;
  }

  // Check if name is empty
  if (!newName.trim()) {
    result.errorMsg = 'Name cannot be empty';
    return result;
  }

  // Get the directory path
  const directory = asset.path.substring(0, asset.path.lastIndexOf('/'));

  // Get file extension from current asset
  const currentExt = asset.name.substring(asset.name.indexOf('.'));

  // Get current name without extension
  const currentNameWithoutExt = asset.name.substring(0, asset.name.indexOf('.'));

  // Check if name is unchanged
  if (newName === currentNameWithoutExt) {
    result.errorMsg = 'New name is the same as current name';
    return result;
  }

  // Validate name follows conventions for asset type
  const regex = getAssetNameRegex(asset.assetType);
  const nameWithoutExt = newName.replace(currentExt, '');

  if (!regex.test(nameWithoutExt)) {
    result.errorMsg = isClassAssetType(asset.assetType)
      ? 'Name must start with an uppercase letter and contain only letters and numbers'
      : 'Name must contain only letters, numbers, hyphens (-) and underscores (_)';
    return result;
  }

  // Build new path
  const newFileName = newName.endsWith(currentExt) ? newName : `${newName}${currentExt}`;
  result.newPath = nameHelpers.joinPaths(directory, newFileName);

  // Check if an asset already exists at the new path (case insensitive)
  const assetFiles = assetsStore.assetTree.getAssetFiles(asset.assetType);
  const conflict = assetFiles.find(a =>
    a.path.toLowerCase() === result.newPath.toLowerCase() &&
    a.path !== asset.path
  );

  if (conflict) {
    result.errorMsg = 'An asset with this name already exists in this directory';
    result.conflicts = [conflict.path];
    return result;
  }

  result.isValid = true;
  return result;
}

/**
 * Rename asset options
 */
export interface RenameAssetOptions {
  asset: IAssetFile;
  newName: string;
  dependencies?: DependencyAnalysisResult; // Pre-computed dependencies (optional)
}

/**
 * Result of rename asset operation
 */
export interface RenameAssetResult {
  success: boolean;
  oldPath: string;
  newPath: string;
  affectedAssets: number;
}

export interface ImportEngineAssetCopyOptions {
  asset: IAssetFile;
  targetDirectory: string;
  targetName: string;
}

export interface ImportEngineAssetCopyResult {
  success: boolean;
  targetPath: string;
}

const DEBUG_RENAME = {
  logRenameStart: true,
  logCacheUpdates: true,
  logPrefabReload: true,
} as const;

/**
 * Update in-memory caches after rename
 */
async function updateCachesAfterRename(oldPath: string, newPath: string): Promise<void> {
  if (DEBUG_RENAME.logCacheUpdates) {
    console.log(`🔄 Updating caches: ${oldPath} → ${newPath}`);
  }

  // Update asset tree cache using MST action
  assetsStore.assetTree.updateAssetFilePath(oldPath, newPath, pathlib.basename(newPath));

  // Update prefab cache if this is a prefab
  assetsStore.updatePrefabPath(oldPath, newPath);

  // Get asset type to determine which engine caches to invalidate
  const asset = assetsStore.assetTree.files.get(newPath);
  if (asset) {
    // Invalidate engine resource caches based on asset type
    switch (asset.assetType) {
      case AssetType.Prefab:
        ENGINE.resourceManager.invalidateJsonObjectCache([oldPath, newPath]);
        if (DEBUG_RENAME.logCacheUpdates) console.log('🗑️ Invalidated prefab json cache:', oldPath, newPath);
        break;
      case AssetType.Material:
        ENGINE.resourceManager.invalidateMaterialCache(oldPath);
        if (DEBUG_RENAME.logCacheUpdates) console.log('🗑️ Invalidated material cache:', oldPath);
        break;
      case AssetType.Texture:
        ENGINE.resourceManager.invalidateTextureCache(oldPath);
        if (DEBUG_RENAME.logCacheUpdates) console.log('🗑️ Invalidated texture cache:', oldPath);
        break;
      case AssetType.Model:
        ENGINE.resourceManager.invalidateModelCache(oldPath);
        if (DEBUG_RENAME.logCacheUpdates) console.log('🗑️ Invalidated model cache:', oldPath);
        break;
      case AssetType.Resource:
        ENGINE.resourceManager.invalidateResourceCache(oldPath);
        if (DEBUG_RENAME.logCacheUpdates) console.log('🗑️ Invalidated resource cache:', oldPath);
        break;
    }
  }

  // Invalidate thumbnail cache for old path
  editorStore.thumbnailService?.invalidateThumbnailCache([oldPath, newPath]);
}

/**
 * Reload affected prefab instances in the current scene
 */
async function reloadAffectedPrefabInstances(oldPath: string, newPath: string): Promise<void> {
  if (!sceneStore.world) return;

  const asset = assetsStore.assetTree.files.get(newPath);
  if (!asset) return;

  if (DEBUG_RENAME.logPrefabReload) {
    console.log('🔄 Reloading affected prefab instances...');
  }

  // If the renamed asset is a prefab, reload its instances
  if (asset.assetType === AssetType.Prefab) {
    // Update instances that still reference the old path
    const oldInstances = sceneStore.world.getActorsByPredicate(
      actor => actor.getPrefabName() === oldPath
    );
    oldInstances?.forEach(actor => {
      sceneActions.reloadActorDeferred(actor);
    });

    // Reload instances with the new path
    prefabActions.reloadPrefabInstances(newPath);
  }
}

async function migrateDefaultSceneSettingsAfterSceneRename(oldPath: string, newPath: string): Promise<void> {
  const { updateGameDefault, updateServerDefault } = resolveDefaultSceneSettingsUpdate({
    oldPath,
    defaultScene: editorStore.defaultScene,
    defaultServerScene: editorStore.defaultServerScene,
  });

  if (!updateGameDefault && !updateServerDefault) {
    return;
  }

  const failedSettings: string[] = [];

  if (updateGameDefault) {
    try {
      await editorStore.setDefaultScene(newPath, 'game');
    } catch (error) {
      failedSettings.push('Game Default Scene');
      console.warn('[renameAsset] Failed to migrate game default scene setting during scene rename:', error);
    }
  }

  if (updateServerDefault) {
    try {
      await editorStore.setDefaultScene(newPath, 'server');
    } catch (error) {
      failedSettings.push('Server Default Scene');
      console.warn('[renameAsset] Failed to migrate server default scene setting during scene rename:', error);
    }
  }

  if (failedSettings.length > 0) {
    toast.warning('Scene renamed with partial settings updates', {
      description: `Failed to update: ${failedSettings.join(', ')}.`,
    });
  }
}

async function migrateSceneThumbnailAfterSceneRename(oldPath: string, newPath: string): Promise<void> {
  const projectFolder = editorStore.project.projectFolder;
  if (!projectFolder || typeof indexedDB === 'undefined') {
    return;
  }

  const migrationTargets = resolveSceneScreenshotMigrationTargets({
    oldScopedKey: getProjectScopedSceneScreenshotIdbKey(projectFolder, oldPath),
    newScopedKey: getProjectScopedSceneScreenshotIdbKey(projectFolder, newPath),
    oldLegacyKey: getLegacySceneScreenshotIdbKey(projectFolder, oldPath),
    newLegacyKey: getLegacySceneScreenshotIdbKey(projectFolder, newPath),
  });

  if (migrationTargets.length === 0) {
    return;
  }

  let db: IDBDatabase | null = null;
  try {
    db = await openIndexedDB();
    for (const target of migrationTargets) {
      const sourceBlob = await loadFromIndexedDB(db, target.sourceKey);
      if (!sourceBlob) {
        continue;
      }
      await saveToIndexedDB(db, target.targetKey, sourceBlob);
      await deleteFromIndexedDB(db, target.sourceKey);
    }
  } catch (error) {
    console.warn('[renameAsset] Failed to migrate scene screenshot keys during scene rename:', error);
  } finally {
    db?.close();
  }

  const renamedSceneAsset = assetsStore.assetTree.findFile(newPath);
  renamedSceneAsset?.refreshThumbnail();
}

/**
 * Rename an asset file
 * @param options - Rename options including asset and new name
 * @returns Result of the rename operation
 */
export async function renameAsset(options: RenameAssetOptions): Promise<RenameAssetResult> {
  const { asset, newName } = options;
  const isPrefabRename = Array.isArray(asset.assetType)
    ? asset.assetType.includes(AssetType.Prefab)
    : asset.assetType === AssetType.Prefab;
  const isSceneRename = Array.isArray(asset.assetType)
    ? asset.assetType.includes(AssetType.Scene)
    : asset.assetType === AssetType.Scene;

  const result: RenameAssetResult = {
    success: false,
    oldPath: asset.path,
    newPath: '',
    affectedAssets: 0,
  };

  try {
    if (DEBUG_RENAME.logRenameStart) {
      console.log(`📝 Starting rename: ${asset.path} → ${newName}`);
    }

    // 1. Validate new name
    const validation = validateRename(asset, newName);
    if (!validation.isValid) {
      toast.error('Invalid name', { description: validation.errorMsg });
      return result;
    }

    result.newPath = validation.newPath;

    // 2. Analyze dependencies (use pre-computed if available)
    const dependencies = options.dependencies ?? await analyzeDependencies(asset.path);
    result.affectedAssets = dependencies.affectedCount;

    // 3. Create update plan
    const updatePlan = await createUpdatePlan(asset.path, validation.newPath);

    // Store paths and directories (asset may become invalid after rename)
    const oldPath = asset.path;
    const oldName = asset.name;
    const oldDirectory = oldPath.substring(0, oldPath.lastIndexOf('/'));
    const newDirectory = validation.newPath.substring(0, validation.newPath.lastIndexOf('/'));

    const storageProvider = editorStore.project.makeScopedStorageProvider();

    const currentScenePath = editorStore.project.currentScene;
    const reloadScenePath = resolveSceneReloadPathAfterRename({
      currentScenePath,
      oldPath,
      newPath: validation.newPath,
      updatePlan,
    });

    // 4. Execute rename (bypasses undo/redo since it's non-reversible)
    // Update all dependent files first
    await executeUpdatePlan(updatePlan);

    // Rename the actual asset file (copy + delete pattern since moveItem doesn't exist)
    const fileContent = await storageProvider.downloadFile(AssetPath.fromString(oldPath));
    await storageProvider.uploadFile(AssetPath.fromString(validation.newPath), fileContent);
    await storageProvider.deleteItem(AssetPath.fromString(oldPath));

    // Update in-memory caches
    await updateCachesAfterRename(oldPath, validation.newPath);

    if (isSceneRename) {
      await migrateSceneThumbnailAfterSceneRename(oldPath, validation.newPath);
      await migrateDefaultSceneSettingsAfterSceneRename(oldPath, validation.newPath);
      // Keep the undo history alive under the new scene path.
      editorStore.undoRedoManager.renameStack(
        resolveSceneUndoStackKey(oldPath),
        resolveSceneUndoStackKey(validation.newPath),
      );
    }

    if (isPrefabRename) {
      editorStore.undoRedoManager.renameStack(
        resolvePrefabUndoStackKey(oldPath),
        resolvePrefabUndoStackKey(validation.newPath),
      );
    }

    // Reload scene if needed, otherwise just reload affected prefab instances
    if (reloadScenePath) {
      if (DEBUG_RENAME.logPrefabReload) {
        console.log('🔄 Reloading scene after asset rename', { reloadScenePath });
      }
      await sceneHandler.loadScene(AssetPath.fromString(reloadScenePath), false);
    } else {
      // Reload affected prefab instances in current scene
      await reloadAffectedPrefabInstances(oldPath, validation.newPath);
    }

    if (isPrefabRename) {
      await prefabActions.autoSaveSceneAfterPrefabMutation('renameAsset');
    }

    // Refresh asset tree directories
    if (oldDirectory === newDirectory) {
      await assetsStore.assetTree.refreshDirectory(oldDirectory);
    } else {
      await assetsStore.assetTree.refreshDirectory(oldDirectory);
      await assetsStore.assetTree.refreshDirectory(newDirectory);
    }

    // Select renamed asset
    editorStore.setSelectedAssetItemByPath(validation.newPath);

    // Notify and show success
    editorActions.notifyChanged('RenameAsset', {
      oldPath: oldPath,
      newPath: validation.newPath
    });

    toast.success('Asset renamed', {
      description: `${oldName} → ${pathlib.basename(validation.newPath)}`
    });

    result.success = true;

  } catch (error) {
    console.error('❌ Failed to rename asset:', { asset, newName, error });
    toast.error('Failed to rename asset', {
      description: error instanceof Error ? error.message : 'Unknown error occurred'
    });
  }

  return result;
}

function splitAssetName(name: string): { baseName: string; extension: string } {
  const firstDotIndex = name.indexOf('.');
  if (firstDotIndex === -1) {
    return { baseName: name, extension: '' };
  }

  return {
    baseName: name.slice(0, firstDotIndex),
    extension: name.slice(firstDotIndex)
  };
}

/**
 * Import a copy of an engine asset into a project directory.
 */
export async function importEngineAssetCopy(options: ImportEngineAssetCopyOptions): Promise<ImportEngineAssetCopyResult | null> {
  const { asset, targetDirectory, targetName } = options;
  const engineAssetsPrefix = `${ENGINE.ENGINE_PATH_PREFIX}/${ENGINE.ASSETS_FOLDER}`;

  if (asset.isFolder) {
    toast.error('Cannot import folder', { description: 'Only file assets can be imported.' });
    return null;
  }

  if (!asset.isEngineAsset) {
    toast.error('Cannot import asset copy', { description: 'Only engine assets can be imported with this option.' });
    return null;
  }

  if (!asset.path.startsWith(engineAssetsPrefix + '/')) {
    toast.error('Cannot import asset copy', {
      description: 'Only assets inside engine assets can be imported with this option.'
    });
    return null;
  }

  if (!targetDirectory.startsWith(ENGINE.PROJECT_PATH_PREFIX)) {
    toast.error('Cannot import asset copy', { description: 'Target directory must be inside the project.' });
    return null;
  }

  const { extension } = splitAssetName(asset.name);
  const validation = checkValidNewAssetName(targetName, targetDirectory, asset.assetType);
  if (!validation.isValid) {
    toast.error('Invalid asset name', { description: validation.errorMsg });
    return null;
  }

  const storageProvider = editorStore.project.makeScopedStorageProvider();

  try {
    let fileList: { files: { isDirectory: boolean; name: string }[] };
    try {
      fileList = await storageProvider.listFiles(AssetPath.fromString(targetDirectory));
    } catch (listError) {
      await storageProvider.createDirectory(AssetPath.fromString(targetDirectory));
      fileList = await storageProvider.listFiles(AssetPath.fromString(targetDirectory));
    }

    const existingNames = fileList.files
      .filter(file => file.isDirectory === false && file.name.endsWith(extension))
      .map(file => extension ? file.name.slice(0, -extension.length) : file.name);
    const uniqueName = nameHelpers.makeUniqueName(targetName, existingNames);
    const targetPath = AssetPath.join(targetDirectory, `${uniqueName}${extension}`);

    const fileContent = await storageProvider.downloadFile(AssetPath.fromString(asset.path));
    await storageProvider.uploadFile(AssetPath.fromString(targetPath), fileContent);

    await assetsStore.assetTree.refreshDirectory(targetDirectory);
    assetsStore.setCurrentDirectory(targetDirectory);
    editorStore.setSelectedAssetItemByPath(targetPath);

    editorActions.notifyChanged('ImportEngineAssetCopy', {
      sourcePath: asset.path,
      targetPath
    });

    toast.success('Asset imported to project', {
      description: `${asset.name} → ${pathlib.basename(targetPath)}`
    });

    return { success: true, targetPath };
  } catch (error) {
    console.error('Failed to import engine asset copy', { assetPath: asset.path, targetDirectory, targetName, error });
    toast.error('Failed to import asset copy', {
      description: error instanceof Error ? error.message : 'Unknown error occurred'
    });
    return null;
  }
}